import { useEffect, useState } from 'react';
import { useInvoiceDetails, useSaveInvoiceDetails } from '../../lib/api/hooks-invoices.js';
import { useSession } from '../../lib/auth/session.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { ApiClientError } from '../../lib/api/client.js';

/**
 * Who an account's invoices are made out to (docs/28 B2): the client's own
 * details, changed with tenant:write. An issued invoice keeps the details it
 * was issued with.
 */
export function InvoiceDetailsCard({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const details = useInvoiceDetails(tenantId);
  const save = useSaveInvoiceDetails(tenantId);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [vat, setVat] = useState('');

  const d = details.data;
  // The form opens on what is saved.
  useEffect(() => {
    if (!d || editing) return;
    setName(d.name ?? '');
    setAddress(d.address ?? '');
    setVat(d.vat_number ?? '');
  }, [d, editing]);

  const fieldErrors = save.error instanceof ApiClientError ? save.error.fieldErrors : {};
  const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());
  const submit = () => void save.mutateAsync({
    name: orNull(name), address: orNull(address), vat_number: orNull(vat),
  }).then(() => setEditing(false), () => undefined);
  const cancel = () => { setEditing(false); save.reset(); };

  return (
    <div className="arc-card" style={{ marginTop: 16 }}>
      <div className="arc-card-head">
        <h2 className="arc-h2">Invoice details</h2>
        {d && !editing && can('tenant:write') && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setEditing(true)}>Change</button>
        )}
      </div>
      <div className="arc-card-body">
        {details.error ? <ErrorBanner error={details.error} />
          : !d ? <div className="arc-skeleton" style={{ height: 60 }} />
          : editing ? (
            <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
              <div className="arc-grid arc-grid-2">
                <div className="arc-field" style={{ margin: 0 }}>
                  <label className="arc-label" htmlFor="invoice-name">Registered name</label>
                  <input id="invoice-name" className="arc-input" value={name} placeholder={d.account_name}
                    onChange={(e) => setName(e.target.value)} aria-invalid={!!fieldErrors['name']} />
                  {fieldErrors['name'] && <div className="arc-error">{fieldErrors['name']}</div>}
                </div>
                <div className="arc-field" style={{ margin: 0 }}>
                  <label className="arc-label" htmlFor="invoice-vat">VAT number</label>
                  <input id="invoice-vat" className="arc-input arc-mono" inputMode="numeric" value={vat}
                    onChange={(e) => setVat(e.target.value)} aria-invalid={!!fieldErrors['vat_number']} />
                  {fieldErrors['vat_number']
                    ? <div className="arc-error">{fieldErrors['vat_number']}</div>
                    : <div className="arc-hint">Leave empty when not VAT registered.</div>}
                </div>
              </div>
              <div className="arc-field" style={{ marginTop: 14 }}>
                <label className="arc-label" htmlFor="invoice-address">Address</label>
                <textarea id="invoice-address" className="arc-input" rows={3} value={address}
                  onChange={(e) => setAddress(e.target.value)} aria-invalid={!!fieldErrors['address']} />
                {fieldErrors['address'] && <div className="arc-error">{fieldErrors['address']}</div>}
              </div>
              <div className="arc-row" style={{ gap: 8 }}>
                <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
                  {save.isPending ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="arc-btn arc-btn-ghost" onClick={cancel}>Cancel</button>
              </div>
            </form>
          ) : (
            <dl className="arc-kv">
              <dt>Made out to</dt>
              <dd>{d.name ?? <>{d.account_name} <span className="arc-subtle">(the account name)</span></>}</dd>
              <dt>Address</dt>
              <dd style={{ whiteSpace: 'pre-line' }}>{d.address ?? <span className="arc-subtle">Not set</span>}</dd>
              <dt>VAT number</dt>
              <dd>{d.vat_number ?? <span className="arc-subtle">Not registered</span>}</dd>
            </dl>
          )}
      </div>
    </div>
  );
}
